export interface StructureNode {
  id: string;
  name: string;
  shortName?: string;
  managerId?: string;
  supervisorId?: string;
  expanded: boolean;
  children: StructureNode[];
}

// ── Flat list format used by react-dnd-treeview ────────────────────

export interface FlatNode {
  id: string;
  parent: string | number;
  text: string;
  droppable: boolean;
}

export function toFlatList(nodes: StructureNode[], parentId: string | number = 0): FlatNode[] {
  const result: FlatNode[] = [];
  for (const n of nodes) {
    result.push({ id: n.id, parent: parentId, text: n.name, droppable: true });
    result.push(...toFlatList(n.children, n.id));
  }
  return result;
}

export function fromFlatList(flat: FlatNode[]): StructureNode[] {
  const map = new Map<string | number, StructureNode>();
  for (const n of flat) {
    map.set(n.id, { id: n.id, name: n.text, expanded: true, children: [] });
  }
  const roots: StructureNode[] = [];
  for (const n of flat) {
    const node = map.get(n.id)!;
    if (n.parent === 0) {
      roots.push(node);
    } else {
      map.get(n.parent)?.children.push(node);
    }
  }
  return roots;
}

export const TEAM_STRUCTURE_LS = "team_structure_v1";
const ASSIGNMENTS_LS = "team_structure_assignments_v1";

let _uid = Date.now();
export const newNodeId = () => `s-${_uid++}`;

const DEFAULT_STRUCTURE: StructureNode[] = [
  { id: "s-root", name: "Organizacja", expanded: true, children: [] },
];

export function loadStructure(): StructureNode[] {
  try {
    const raw = localStorage.getItem(TEAM_STRUCTURE_LS);
    if (!raw) return DEFAULT_STRUCTURE;
    const parsed: StructureNode[] = JSON.parse(raw);
    return parsed.length > 0 ? parsed : DEFAULT_STRUCTURE;
  } catch { return DEFAULT_STRUCTURE; }
}

export function saveStructure(tree: StructureNode[]): void {
  try {
    localStorage.setItem(TEAM_STRUCTURE_LS, JSON.stringify(tree));
    window.dispatchEvent(new CustomEvent("structureChanged"));
  } catch { /* ignore */ }
}

export function flattenStructure(
  nodes: StructureNode[],
  depth = 0,
): Array<{ id: string; name: string; depth: number; hasChildren: boolean }> {
  const result: Array<{ id: string; name: string; depth: number; hasChildren: boolean }> = [];
  for (const n of nodes) {
    result.push({ id: n.id, name: n.name, depth, hasChildren: n.children.length > 0 });
    result.push(...flattenStructure(n.children, depth + 1));
  }
  return result;
}

export function updateStructureNode(
  nodes: StructureNode[],
  id: string,
  fn: (n: StructureNode) => StructureNode,
): StructureNode[] {
  return nodes.map((n) =>
    n.id === id ? fn(n) : { ...n, children: updateStructureNode(n.children, id, fn) },
  );
}

export function removeStructureNode(nodes: StructureNode[], id: string): StructureNode[] {
  return nodes
    .filter((n) => n.id !== id)
    .map((n) => ({ ...n, children: removeStructureNode(n.children, id) }));
}

export function addChildToNode(
  nodes: StructureNode[],
  parentId: string,
  child: StructureNode,
): StructureNode[] {
  return updateStructureNode(nodes, parentId, (n) => ({
    ...n,
    expanded: true,
    children: [...n.children, child],
  }));
}

// ── Przynależność do struktury ──────────────────────────────────────

export interface StructureAssignment {
  personName: string;
  nodeIds: string[];
}

export function loadAssignments(): StructureAssignment[] {
  try {
    return JSON.parse(localStorage.getItem(ASSIGNMENTS_LS) || "[]");
  } catch { return []; }
}

export function saveAssignment(personName: string, nodeIds: string[]): void {
  try {
    const all = loadAssignments().filter((a) => a.personName !== personName);
    if (nodeIds.length > 0) all.push({ personName, nodeIds });
    localStorage.setItem(ASSIGNMENTS_LS, JSON.stringify(all));
  } catch { /* ignore */ }
}

export function getAssignment(personName: string): string[] {
  return loadAssignments().find((a) => a.personName === personName)?.nodeIds ?? [];
}
